import { useLanguage } from "@/hooks/useLanguage";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Eye } from "lucide-react";

interface LanguageSwitcherProps {
  className?: string;
  variant?: "default" | "minimal" | "compact";
}

const LanguageSwitcher = ({ className, variant = "default" }: LanguageSwitcherProps) => {
  const { language, setLanguage, isArabicEnabled, isArabicPreview, isArabicLive, isAdmin, loading, t } = useLanguage();

  // Don't show switcher if Arabic is not enabled for this user
  if (!isArabicEnabled || loading) {
    return null;
  }

  // Show preview indicator only for admins in preview mode
  const showPreviewBadge = isArabicPreview && !isArabicLive && isAdmin;

  if (variant === "compact") {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setLanguage(language === "en" ? "ar" : "en")}
        aria-label={language === "en" ? t("lang.switchToArabic") : t("lang.switchToEnglish")}
        className={cn("h-9 px-2 gap-1 font-medium text-sm", className)}
      >
        {showPreviewBadge && <Eye className="h-3 w-3 text-amber-500" />}
        {language === "en" ? "عربي" : "EN"}
      </Button>
    );
  }

  if (variant === "minimal") {
    return (
      <div className="flex items-center gap-1">
        {showPreviewBadge && (
          <Eye className="h-3 w-3 text-amber-500" />
        )}
        <button
          onClick={() => setLanguage(language === "en" ? "ar" : "en")}
          className={cn(
            "flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors",
            className
          )}
        >
          <span className={cn(language === "en" && "text-gold font-semibold")}>EN</span>
          <span className="text-border">|</span>
          <span className={cn(language === "ar" && "text-gold font-semibold")}>عربي</span>
        </button>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {showPreviewBadge && (
        <Eye className="h-3 w-3 text-amber-500 mr-1" />
      )}
      <div className="flex items-center gap-1 bg-muted/30 rounded-md p-0.5">
        <Button
          variant={language === "en" ? "default" : "ghost"}
          size="sm"
          onClick={() => setLanguage("en")}
          className={cn(
            "h-7 px-2.5 text-xs font-medium transition-all",
            language === "en" 
              ? "bg-gold text-gold-foreground hover:bg-gold/90" 
              : "hover:bg-muted/50"
          )}
        >
          EN
        </Button>
        <Button
          variant={language === "ar" ? "default" : "ghost"}
          size="sm"
          onClick={() => setLanguage("ar")}
          className={cn(
            "h-7 px-2.5 text-xs font-medium transition-all",
            language === "ar" 
              ? "bg-gold text-gold-foreground hover:bg-gold/90" 
              : "hover:bg-muted/50"
          )}
        >
          عربي
        </Button>
      </div>
    </div>
  );
};

export default LanguageSwitcher;
